import { pxToRem } from '@back-office/core';
import {
  RecurringRequestListPagination,
  RecurringTransactionsList,
} from '@back-office/http';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Pagination from '@material-ui/lab/Pagination';
import { observer } from 'mobx-react';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyIcon } from '../../icons';
import { getDatetimeText } from '../../utils';
import GcpBox from '../gcp-box';
import GcpCurrencyFormat from '../gcp-currency-format';
import GcpTransactionRecurringStatus, {
  TransactionRecurringStatus,
} from '../gcp-transaction-recurring-status';
import GcpTypography from '../gcp-typography';
import tableHeader from './content';

const useStyles = makeStyles((theme) => ({
  table: {
    minWidth: 1400,
    borderCollapse: 'separate',
  },
  headerCell: {
    padding: theme.spacing(2, 0, 1, 0),
    backgroundColor: theme.palette.common.lightGray,
    '&:first-child': {
      position: 'sticky',
      left: 0,
      width: '9.8%',
      boxShadow: '0px 8px 4px 0px rgba(0,0,0,0.1);',
    },
    '&:last-child': {
      position: 'sticky',
      right: 0,
      width: '10%',
      boxShadow: '0px 8px 4px 0px rgba(0,0,0,0.1);',
    },
  },
  bodyRow: {
    verticalAlign: 'top',
    cursor: 'pointer',
  },
  bodyCell: {
    border: 'none',
    '&:first-child': {
      position: 'sticky',
      left: 0,
      width: '6%',
      backgroundColor: 'white',
      boxShadow: '0px 8px 4px 0px rgba(0,0,0,0.1);',
    },
    '&:last-child': {
      position: 'sticky',
      right: 0,
      backgroundColor: 'white',
      boxShadow: '0px 8px 4px 0px rgba(0,0,0,0.1);',
    },
    '& > p': {
      overflow: 'hidden',
      wordBreak: 'break-all',
    },
  },
  avatar: {
    width: pxToRem(14),
    height: pxToRem(14),
  },
  alert: {
    background: '#FFF1F0',
    border: '1px solid #FFA39E',
    boxSizing: 'border-box',
    borderRadius: '2px',
  },
  empty: {
    backgroundColor: 'white',
    '& > svg': {
      width: '10rem',
      height: '10rem',
      fill: 'none',
    },
  },
}));

interface GcpTransactionsRecurringTableProps {
  data: RecurringTransactionsList[];
  pagination: RecurringRequestListPagination;
  page: number;
  count: number;
  handleOnChangePage: (newPage: number) => void;
}

const GcpTransactionsRecurringTable: FC<GcpTransactionsRecurringTableProps> =
  observer((props) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { data, pagination, page, count, handleOnChangePage } = props;

    return (
      <>
        <TableContainer component="div" style={{ maxWidth: 1096 }}>
          <Table
            aria-label="GCP Transaction recurring"
            className={classes.table}
          >
            <TableHead>
              <TableRow>
                {tableHeader.map((cell) => (
                  <TableCell
                    key={t(cell.title)}
                    align="left"
                    className={classes.headerCell}
                  >
                    <GcpBox ml={1}>
                      <GcpTypography
                        paint="mineShaft"
                        variant="body1"
                        whiteSpace="pre-line"
                      >
                        {t(cell.title)}
                      </GcpTypography>
                    </GcpBox>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {data?.map((item) => (
                <TableRow key={item.id} hover className={classes.bodyRow}>
                  <TableCell className={classes.bodyCell}>
                    <GcpTypography paint="mineShaft" variant="body1">
                      {item.transaction_at
                        ? getDatetimeText(item.transaction_at, 'default')
                        : 'N/A'}
                    </GcpTypography>
                  </TableCell>
                  <TableCell className={classes.bodyCell} width="11%">
                    <GcpTypography paint="mineShaft" variant="body1">
                      {item.wallet_id || 'N/A'}
                    </GcpTypography>
                  </TableCell>
                  <TableCell className={classes.bodyCell} width="11%">
                    <GcpTypography paint="mineShaft" variant="body1">
                      {item.mobile_no || 'N/A'}
                    </GcpTypography>
                  </TableCell>
                  <TableCell className={classes.bodyCell} width="11%">
                    <GcpTypography paint="mineShaft" variant="body1">
                      {item.transaction_id || 'N/A'}
                    </GcpTypography>
                  </TableCell>
                  <TableCell className={classes.bodyCell} width="11%">
                    <GcpTypography paint="mineShaft" variant="body1">
                      {item.merchant_tx_short_id || 'N/A'}
                    </GcpTypography>
                  </TableCell>
                  <TableCell className={classes.bodyCell} width="11%">
                    <GcpTypography paint="mineShaft" variant="body1">
                      {item.reference_number}
                    </GcpTypography>
                  </TableCell>
                  <TableCell className={classes.bodyCell} width="8%">
                    <GcpTypography paint="mineShaft" variant="body1">
                      <GcpCurrencyFormat
                        fixedDecimalScale
                        paint="mineShaft"
                        value={item.amount_string}
                        valueVariant="body1"
                      />
                    </GcpTypography>
                  </TableCell>
                  <TableCell className={classes.bodyCell} width="11%">
                    <GcpTypography paint="mineShaft" variant="body1">
                      {item.batch_name}
                    </GcpTypography>
                  </TableCell>
                  <TableCell className={classes.bodyCell} width="16%">
                    <GcpBox alignItems="center" display="flex" gap={1}>
                      <GcpTransactionRecurringStatus
                        size={8}
                        status={
                          item.status?.toLowerCase() as TransactionRecurringStatus
                        }
                      />
                      <GcpTypography paint="mineShaft" variant="body1">
                        {t(
                          `payment_file.transactions_list.status.${item.status?.toLowerCase()}`
                        )}
                        {item.status?.toLowerCase() ===
                          TransactionRecurringStatus.failed &&
                          `: ${item.reject_reason}`}
                      </GcpTypography>
                    </GcpBox>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {data.length === 0 && (
          <GcpBox
            isFullWidth
            alignItems="center"
            className={classes.empty}
            display="flex"
            flexDirection="column"
            mt={8}
          >
            <EmptyIcon className={classes.empty} />
            <GcpTypography
              paint="hitGray"
              textAlign="center"
              variant="body1"
              whiteSpace="pre-line"
            >
              {t('payment_file.no_data')}
            </GcpTypography>
          </GcpBox>
        )}
        {data.length !== 0 && (
          <GcpBox display="flex" justifyContent="flex-end" mb={4} mt={4}>
            <GcpBox alignItems="center" display="flex" mr={2.5}>
              <GcpTypography variant="body1">
                {t('biller_enrollment_list.viewing', {
                  start: (pagination.current_page - 1) * count + 1,
                  end: Math.min(
                    pagination.current_page * count,
                    pagination.total_count
                  ),

                  total: pagination.total_count,
                })}
              </GcpTypography>
            </GcpBox>
            <Pagination
              color="primary"
              count={Math.ceil(pagination.total_count / count)}
              page={page}
              shape="rounded"
              variant="outlined"
              onChange={(_event, pageNumber) => {
                handleOnChangePage(pageNumber);
              }}
            />
          </GcpBox>
        )}
      </>
    );
  });

export default GcpTransactionsRecurringTable;
